/**
 * InGo's deliveries in the shapes the rider's screens already read (the
 * mobile plan's M5; InGo ADR 0012: the screens stay, their data source
 * changes). An offer becomes the offer object the offer screens read, a
 * delivery the job object `useDriverJob` hands them, marked `source: 'api'`.
 *
 * Nothing here calls the network; `src/api/riderWork.js` does.
 */

/** The table label in a job's route key (`deliveries:42`): InGo's deliveries. */
export const API_DELIVERIES = 'deliveries';

const SIZES = { small: 'Small', medium: 'Medium', large: 'Large', xlarge: 'Extra large' };
const CATEGORIES = {
  documents: 'Documents',
  electronics: 'Electronics',
  clothing: 'Clothing',
  food: 'Food',
  fragile: 'Fragile',
  other: 'Other',
};

/** The app's cancel reasons (`driverBookingCancel.js`) as the API names a hand-back's. */
export const HAND_BACK_REASONS = {
  breakdown: 'breakdown',
  client_not_reachable: 'client_unreachable',
  wrong_address: 'wrong_address',
  customer_refused: 'client_refused',
  safety: 'safety',
  traffic_blocked: 'road_blocked',
  other: 'other',
};

/** Whether this offer or job came from InGo's API. */
export function isApiWork(item) {
  return item?.source === 'api';
}

function sizeLabel(size) {
  return SIZES[size] || (size ? String(size) : '—');
}

function categoryLabel(pkg) {
  if (!pkg?.category) return 'Delivery';
  if (pkg.category === 'other' && pkg.category_other) return pkg.category_other;

  return CATEGORIES[pkg.category] || String(pkg.category);
}

/** The app's own rule (`offerProximity.formatDistanceKm`), copied so this stays free of the geocoder. */
function kilometres(km) {
  const n = Number(km);

  if (km == null || !Number.isFinite(n) || n <= 0) return '';
  if (n < 1) return `${Math.max(50, Math.round((n * 1000) / 50) * 50)} m`;
  if (n < 10) return `${n.toFixed(1)} km`;

  return `${Math.round(n)} km`;
}

/** Minor units to the major units every rider screen formats (`formatGBP`). */
function major(minor) {
  const n = Number(minor);

  return Number.isFinite(n) ? n / 100 : 0;
}

/**
 * An API offer (`GET rider/me/offers`) as the offer screens read one. Its key
 * (`deliveries:<delivery id>`) is the job's too; the offer's own id, which
 * accepting and declining need, is `offerId`. Cash on delivery is the app's
 * `cod`. No minimum fare: the price is the one the client booked at.
 */
export function offerFromApi(offer) {
  const d = offer.delivery || {};
  const offeredAt = offer.offered_at || null;
  const window = offeredAt && offer.expires_at ? Math.round((Date.parse(offer.expires_at) - Date.parse(offeredAt)) / 1000) : null;

  return {
    source: 'api',
    table: API_DELIVERIES,
    kind: 'parcel',
    id: d.id,
    offerId: offer.id,
    ref: d.reference || `D-${d.id}`,
    from: d.pickup?.address || '—',
    to: d.dropoff?.address || '—',
    pickupPoint: d.pickup ? { lat: d.pickup.latitude, lng: d.pickup.longitude } : null,
    dist: kilometres(d.distance_km),
    eta: '',
    pkg: [categoryLabel(d.package), sizeLabel(d.package?.size)].join(' · '),
    amount: major(d.total_minor),
    minimumAmount: undefined,
    created_at: offeredAt,
    expiresAt: offer.expires_at || null,
    windowSeconds: window && window > 0 ? window : null,
    customerPayment: 'Cash Payment',
    raw: { created_at: offeredAt, payment_method: 'cod', assigned_driver_id: null },
  };
}

/** Seconds left to answer an API offer, from its own `expires_at`: never below zero. */
export function apiOfferSecondsLeft(offer, now = Date.now()) {
  if (!offer?.expiresAt) return 0;

  return Math.max(0, Math.ceil((Date.parse(offer.expiresAt) - now) / 1000));
}

/**
 * An API delivery (`rider/me/deliveries`) as the job object the job screens
 * read. The pins' contacts are the rider's now the job is theirs: the pickup's
 * as the "Sender" card, the drop-off's for the drop-off leg. The client's own
 * package photo is not sent to riders; the rider's is kept on the server once
 * picked up (`hasPickupPhoto`).
 */
export function jobFromApi(d) {
  return {
    source: 'api',
    id: d.reference || `D-${d.id}`,
    deliveryId: d.id,
    bookingTable: API_DELIVERIES,
    bookingKind: 'parcel',
    status: d.status,
    statusLabel: d.status_label || '',
    from: d.pickup?.address || '—',
    to: d.dropoff?.address || '—',
    pickupPoint: d.pickup ? { lat: d.pickup.latitude, lng: d.pickup.longitude } : null,
    dropoffPoint: d.dropoff ? { lat: d.dropoff.latitude, lng: d.dropoff.longitude } : null,
    dist: kilometres(d.distance_km),
    eta: '',
    distDrop: '',
    etaDrop: '',
    pkg: sizeLabel(d.package?.size),
    type: categoryLabel(d.package),
    size: sizeLabel(d.package?.size),
    packageWeight: d.package?.weight_kg ?? null,
    amount: major(d.total_minor),
    currency: d.currency || '',
    customerName: d.pickup?.contact_name || 'Sender',
    customerPhone: d.pickup?.contact_phone || '',
    dropoffContactName: d.dropoff?.contact_name || '',
    dropoffContactPhone: d.dropoff?.contact_phone || '',
    customerPayment: 'Cash Payment',
    payment_method: 'cod',
    specialInstructions: d.package?.notes || '',
    packagePhotoDataUrl: null,
    driverPackagePhotoDataUrl: null,
    hasPickupPhoto: Boolean(d.has_pickup_photo),
    mayHandBack: Boolean(d.may_hand_back),
    mayPickUp: Boolean(d.may_pick_up),
    mayDropOff: Boolean(d.may_drop_off),
    codeLocked: Boolean(d.code_locked),
    earning: d.earning ? major(d.earning.rider_minor) : null,
    acceptedAt: d.accepted_at || null,
  };
}

/** Whether the rider is on the drop-off leg: the server says it is picked up. */
export function isOnDropOffLeg(job) {
  return isApiWork(job) && job.status === 'picked_up';
}

/** A job carried now (the only one a rider may continue). */
export function isCarried(d) {
  return d?.status === 'accepted' || d?.status === 'picked_up';
}

/** A delivery as a row of the Orders page's "Active" list. */
export function orderRowFromApi(d, formatTime) {
  const job = jobFromApi(d);

  return {
    id: job.id,
    from: job.from,
    to: job.to,
    pkg: [job.type, job.size].join(' · '),
    amount: job.amount,
    lineStatus: job.statusLabel,
    lineTime: formatTime(d.accepted_at),
    order: job,
  };
}

/** A delivery as a row of the home page's "Your Recent Jobs". */
export function recentFromApi(d) {
  const at = d.delivered_at || d.picked_up_at || d.accepted_at || null;

  return {
    id: d.id,
    ref: d.reference || `D-${d.id}`,
    to: d.dropoff?.address || '—',
    amt: d.earning ? major(d.earning.rider_minor) : major(d.total_minor),
    st: d.status_label || String(d.status || ''),
    t: at,
    assignedAt: d.accepted_at || null,
    kind: 'parcel',
  };
}

/** Refusals that mean the offer is gone for this rider, not that something failed. */
const GONE = ['DELIVERY_TAKEN', 'OFFER_NO_LONGER_OPEN', 'NOT_FOUND'];

/** What the rider is told when the server gave no words of its own for a gone offer. */
export const ORDER_ALREADY_ACCEPTED_MSG = 'This order has already been accepted by another driver.';

/**
 * The one accept path for an API offer (the offer screen, the home card, the
 * notification's button): accept by the offer's id, then read the job it
 * became. `taken` when another rider has it or the offer closed; a job
 * that could not be read yet is still the rider's (`job: null`, opened by its
 * key, and `useDriverJob` reads it).
 *
 * @param {{accept: Function, delivery: Function}} work `src/api/riderWork.js`, or a fake
 */
export async function acceptApiOffer(work, offer) {
  try {
    await work.accept(offer.offerId);
  } catch (error) {
    return { ok: false, taken: GONE.includes(error?.code), error: error?.message || 'Could not accept this request.' };
  }

  try {
    return { ok: true, job: jobFromApi(await work.delivery(offer.id)) };
  } catch {
    return { ok: true, job: null };
  }
}

/** The one decline path for an API offer. */
export async function declineApiOffer(work, offer, reason = null) {
  try {
    await work.decline(offer.offerId, reason);

    return { ok: true };
  } catch (error) {
    // Already closed: nothing left to decline, and nothing to tell the rider but that.
    if (GONE.includes(error?.code)) return { ok: true, gone: true };

    return { ok: false, error: error?.message || 'Could not save your decline.' };
  }
}

/** The photo the capture component made (a `data:` URL) as the Blob an upload sends. */
export function dataUrlToBlob(dataUrl) {
  const match = /^data:([^;,]+)(;base64)?,(.*)$/s.exec(String(dataUrl || ''));

  if (!match) throw new Error('The photo could not be read. Take it again.');

  const [, type, base64, body] = match;
  const binary = base64 ? atob(body) : decodeURIComponent(body);
  const bytes = new Uint8Array(binary.length);

  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

  return new Blob([bytes], { type });
}
